import { useContext, useEffect, useId, useState } from "react";
import { Context } from "../../../context/Context";
import { FileText, Input, NoticeBox, Radio, Select, Sparkles, Upload } from "../../ui";
import { ActionDialog, DialogFooter, menuOptionLabel } from "../topActions/shared";
import { fetchAiImportConfigAPI, uploadAiImportAPI } from "../../../utils/api/menuAiImport";
import AiBalanceNotice, { aiWalletNoticeOf } from "../../billing/aiWallet/AiBalanceNotice";
import { notifyAiWalletChanged } from "../../billing/aiWallet/aiWalletMenu";
import { aiImportText } from "./aiImportText";
import { menuErrorMessage } from "../menuRole";
import "./AiImport.css";
import { aiImportErrorMessage, baseName } from "./aiImportModel";

const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.xlsx,.xlsm,.csv,image/jpeg,image/png,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";
const EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".pdf", ".xlsx", ".xlsm", ".csv"];
const MODES = ["new_menu", "add_to_menu", "update_menu"];

// Upload step of AI Menu Import. The file is checked again (by content) on
// the server; this only gives quick feedback. `onUploaded(draft, duplicate)`.
export default function AiImportUploadDialog({ authorize, restaurantId, menus, initialMenuId, onClose, onUploaded }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const inputId = useId();
  const [config, setConfig] = useState(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("new_menu");
  const [menuId, setMenuId] = useState("");
  const [menuName, setMenuName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The AI Wallet of the restaurant refused the import (nothing was uploaded).
  const [walletNotice, setWalletNotice] = useState(null);
  const [dragging, setDragging] = useState(false);
  const targetMenus = menus.filter((m) => m.lifecycle !== "archived");
  const maxMb = Math.round((config?.maxFileBytes || 15 * 1024 * 1024) / 1024 / 1024);

  useEffect(() => {
    let alive = true;
    fetchAiImportConfigAPI()
      .then((value) => alive && setConfig(value))
      .catch(() => alive && setConfig({ enabled: true }));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (mode !== "new_menu" && !menuId) setMenuId(String(targetMenus.find((m) => String(m._id) === String(initialMenuId))?._id || targetMenus[0]?._id || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const pick = (next) => {
    setError("");
    if (!next) return;
    const extension = `.${String(next.name).split(".").pop()}`.toLowerCase();
    if (!EXTENSIONS.includes(extension)) return setError(t.fileUnsupported);
    if (config?.maxFileBytes && next.size > config.maxFileBytes) return setError(t.fileTooLarge(maxMb));
    setFile(next);
    if (!menuName) setMenuName(baseName(next.name));
    return undefined;
  };

  const submit = async () => {
    // Checked again right before the upload (NA-010): the restaurant of this import.
    if (!authorize?.()) return setError(menuErrorMessage({ status: 403 }, lang));
    setBusy(true);
    setError("");
    setWalletNotice(null);
    try {
      const result = await uploadAiImportAPI({ file, restaurant: restaurantId, mode, menuId: mode === "new_menu" ? "" : menuId, menuName: mode === "new_menu" ? menuName.trim() : "" });
      onUploaded(result.import, result.duplicate);
    } catch (err) {
      const notice = aiWalletNoticeOf(err);
      setWalletNotice(notice);
      // The refusal carries the current balance: the indicator re-reads it too.
      if (notice) notifyAiWalletChanged();
      else setError(aiImportErrorMessage(err, t, lang));
      setBusy(false);
    }
  };

  const disabled = config?.enabled === false || !file || (mode === "new_menu" ? !menuName.trim() : !menuId);

  return (
    <ActionDialog title={t.uploadTitle} busy={busy} error={error} onClose={onClose} footer={<DialogFooter submitLabel={t.upload} onSubmit={submit} onCancel={onClose} busy={busy} disabled={disabled} />}>
      <p className="menu-note">{t.uploadHint}</p>
      {config?.enabled === false && (
        <NoticeBox variant="danger" size="sm">
          {t.notConfigured}
        </NoticeBox>
      )}
      <AiBalanceNotice notice={walletNotice} lang={lang} />
      <label
        htmlFor={inputId}
        className={`ai-import-dropzone ${dragging ? "is-dragging" : ""} ${file ? "has-file" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pick(e.dataTransfer?.files?.[0]);
        }}
      >
        <span className="ai-import-dropzone-icon" aria-hidden="true">
          {file ? <FileText size={26} /> : <Upload size={26} />}
        </span>
        <strong>{file ? file.name : t.dropHere}</strong>
        <span className="menu-secondary">{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : t.accepted(maxMb)}</span>
        <span className="ui-button primary soft sm rounded">
          <Sparkles size={15} /> {file ? t.replaceFile : t.chooseFile}
        </span>
        <input id={inputId} type="file" accept={ACCEPT} className="ai-import-file-input" aria-label={t.chooseFile} disabled={busy} onChange={(e) => pick(e.target.files?.[0])} />
      </label>

      <fieldset className="ai-import-modes" disabled={busy}>
        <legend className="menu-action-step">{t.intent}</legend>
        {MODES.map((value) => (
          <Radio key={value} name="ai-import-mode" value={value} label={t.modes[value]} checked={mode === value} disabled={value !== "new_menu" && !targetMenus.length} onChange={() => setMode(value)} />
        ))}
        <p className="menu-note">{t.modeHints[mode]}</p>
      </fieldset>

      {mode === "new_menu" ? (
        <Input label={t.menuName} value={menuName} maxLength={120} required onChange={(e) => setMenuName(e.target.value)} disabled={busy} />
      ) : (
        <Select label={t.targetMenu} placeholder={t.selectMenu} value={menuId} options={targetMenus.map((m) => ({ value: String(m._id), label: menuOptionLabel(m, lang) }))} onChange={(e) => setMenuId(e.target.value)} disabled={busy} />
      )}
    </ActionDialog>
  );
}
